import {
  type ConditionGroup,
  type WorkflowAction,
  type WorkflowScope,
  type WorkflowTrigger,
} from './automation.js';
import {
  GRAPH_SCHEMA_VERSION,
  graphActionType,
  isGraphTriggerKind,
  type GraphActionKind,
  type GraphEdge,
  type GraphNode,
  type WorkflowGraph,
} from './automation-graph.js';

/**
 * v1 ↔ v2 DÖNÜŞTÜRÜCÜLERİ — doğrusal kural ile grafik arasındaki iki yön.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 NEDEN DÖNÜŞTÜRÜCÜ, NEDEN VERİ GÖÇÜ DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Veritabanındaki kurallar bugün `schemaVersion: 1`: `Workflow` tablosunda
 * `trigger`, `conditions`, `actions`, `scope` AYRI Json kolonları. 40 şablon
 * (`template-seed.ts`) da o şekilde yazıldı.
 *
 * Motor ise YALNIZ GRAFİK YÜRÜTECEK. v1 için ikinci bir yürütme yolu
 * yazılmasının neden reddedildiği `automation.engine.ts` başlığında yazılı ve
 * tek cümlesi şu: KURU ÇALIŞTIRMA İLE GERÇEK KOŞU AYNI KODU ÇALIŞTIRMAK
 * ZORUNDA. İki yol, kullanıcının kuru çalıştırmada gördüğünden farklı bir
 * sonuç uygulaması demek — para hareket ettiren bir üründe güvenin bittiği
 * yer. Aynı gerekçe v1/v2 ayrımına da aynen uyuyor: "eski kurallar şu koddan,
 * yeniler bu koddan" iki karar merkezi demekti.
 *
 * Dolayısıyla v1 kuralları BELLEKTE v2'ye çevriliyor. Veri göçü YOK ve bu
 * bilinçli:
 *
 *   • Göç tek yönlü ve geri alınamaz. Motor grafikte bir hata çıkarırsa v1
 *     satırlar zaten dönüşmüş olurdu; geri dönecek veri kalmazdı.
 *   • v1 kolonları OKUNMAYA DEVAM EDİYOR — liste kartı, şablon kurulumu,
 *     admin ekranları. Göç onları boşaltırdı; boşalan kolonları dolduran
 *     ikinci bir yazıcı, tam olarak bu dosyanın ikinci fonksiyonu olurdu.
 *   • Dönüşüm SAF ve DETERMİNİSTİK olduğu için "kayıtlı hâli" ile "hesaplanan
 *     hâli" diye iki gerçek doğmuyor. Kayıtlı grafik yoksa hesaplanan grafik
 *     her seferinde aynı.
 *
 * ---------------------------------------------------------------------------
 * 🔴 DETERMİNİZM BİR ZEVK MESELESİ DEĞİL
 * ---------------------------------------------------------------------------
 * `Math.random()` ve `Date.now()` bu dosyada YASAK. Aynı v1 kural her çağrıda
 * BAYT BAYT AYNI grafiği vermek zorunda, çünkü:
 *
 *   1. Aksiyonun `actionIndex`i zincirdeki sırasından geliyor ve o indeks
 *      `actionIdempotencyKey`in bileşeni (bkz. `automation-core/idempotency.ts`).
 *      Sıra kayarsa retry ESKİ log satırına çarpmaz, YENİ satır açar ve
 *      platforma İKİNCİ bir bütçe artışı gönderir. Kullanıcı %10 yerine %21
 *      bulur ve geri alınamaz.
 *   2. Kuru çalıştırma ile gerçek koşu aynı grafiği görmek zorunda. Rastgele
 *      düğüm kimliği, iki koşunun farklı grafikleri olması demekti.
 *   3. `position` bile deterministik: aynı kuralı iki kez açan kullanıcı aynı
 *      tuvali görmeli. Sabit adımlı yatay dizilim, "her açılışta kutular
 *      oynuyor" şikâyetini doğurmaz.
 *
 * ---------------------------------------------------------------------------
 * Bu dosya ZOD ÇAĞIRMIYOR
 * ---------------------------------------------------------------------------
 * Ne `graphFromLegacy` girdiyi `parse` ediyor ne de `projectionFromGraph`
 * çıktıyı. Sebep tek: `parse` VARSAYILAN uyguluyor ve gidiş-dönüş kimliğini
 * bozardı — `{type:'pause'}` ile giren aksiyon, dönüşte varsayılanlarla
 * şişmiş bir nesne olarak çıkardı ve `Workflow.actions` kolonu her kaydetmede
 * sessizce büyürdü. Doğrulama KAPILARDA yapılıyor (`workflowUpsertSchema`
 * kaydetmede, `workflowGraphSchema` grafik kaydetmede); burası bir kapı değil,
 * bir dizilim değişikliği.
 *
 * ⚠️ İkisi de nesneleri KOPYALAMADAN taşıyor: üretilen grafiğin `data`
 * alanları çağıranın verdiği nesnelerin ta kendisi. Derin kopya ne gidiş-dönüş
 * kimliğine bir şey katıyor ne de bir hatayı engelliyor; ama çağıran dönen
 * nesneyi MUTASYONA UĞRATMAMALI, yoksa kaynağı da değiştirir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Düğüm kimlikleri ve yerleşim
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 SABİT KİMLİKLER — dize değişmezi olarak tekrarlanmaz.
 *
 * Motor, kuru çalıştırma ve koşu kaydı bu kimlikleri okuyor ("hangi düğümde
 * durdu"). Üç yerde ayrı ayrı `'scope'` yazılsaydı biri değiştiğinde diğerleri
 * sessizce eski kimliği aramaya devam ederdi.
 *
 * Alfabe `GRAPH_NODE_ID_PATTERN` ile uyumlu (harf, rakam, `_`, `-`).
 */
export const LEGACY_NODE_IDS = {
  trigger: 'trigger',
  scope: 'scope',
  filter: 'filter',
} as const;

/**
 * Aksiyon düğümünün kimliği — `order` 1'den başlar.
 *
 * 1 tabanlı çünkü kullanıcıya gösterilen sıra da 1 tabanlı ("3. aksiyon");
 * kimlikte 0 tabanlı bir sayı, koşu kaydını okuyanın kafasında bir kaydırma
 * yaratırdı.
 */
export function legacyActionNodeId(order: number): string {
  return `action-${order}`;
}

/**
 * Kenar kimliği — uçlardan TÜRETİLİYOR, sayaçla değil.
 *
 * `e-scope-filter` bir hata mesajında (`GRAPH_EDGE_UNKNOWN_NODE`) tek başına
 * okunabiliyor; `e-3` okunamıyor. Doğrusal zincirde her (from, to) çifti bir
 * kez geçtiği için kimlikler ayrıca tekilliği de garanti ediyor.
 */
function legacyEdgeId(from: string, to: string): string {
  return `e-${from}-${to}`;
}

/// Tuvalde YATAY adım. Akış soldan sağa okunuyor: düğüm kutusu 300px, kalan
/// 120 hem kenarı hem dal etiketini ("Koşul tutarsa" — kartın sağ kenarının
/// dışına yazılıyor) alıyor. Daha dar bir adımda etiket sonraki kartın altında
/// kalıyordu.
///
/// 🔴 Eskiden dikeydi (`LEGACY_LAYOUT_STEP_Y`, 160). Yön değişti çünkü tuval
/// bağlantıları artık kutunun SAĞ kenarından çıkıp SOL kenarından giriyor
/// (n8n modeli, 17 Ağu 2026). Dikey bir kolon o bağlantı noktalarıyla her
/// kenarda geriye kıvrılan bir kavis üretiyordu. Yerleşim yalnız ilk açılışı
/// belirliyor — kullanıcının sürüklediği konumlar grafik belgesinde saklı ve
/// bu sabit onlara dokunmuyor.
export const LEGACY_LAYOUT_STEP_X = 420;
/// Tek satır — doğrusal kuralın grafikte de tek satır olması, dönüşümün
/// "aynı şeyin ikinci dizilimi" olduğunu ekranda da söylüyor.
export const LEGACY_LAYOUT_Y = 0;

// ═══════════════════════════════════════════════════════════════════════════
// v1 → v2
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Doğrusal kuralı grafiğe açar:
 *
 *     trigger → scope → filter → action₁ → action₂ → … → actionₙ
 *
 * ---------------------------------------------------------------------------
 * Neden ZİNCİR, aksiyonlar `filter`dan paralel çıkmıyor
 * ---------------------------------------------------------------------------
 * Şekil denetleyicisi bir düğümden birden fazla kenar çıkmasını yasaklamıyor;
 * yani `filter`dan on aksiyona birden bağlanmak MÜMKÜN ve grafiği üç düğüm
 * derinliğinde tutardı. Reddedildi: v1'de aksiyonlar SIRALI uygulanıyor
 * (`workflowActionsSchema` başlığı — "sunucu diziyi DİZİ SIRASINA göre
 * uygular"). Paralel dizilim o sırayı kenar dizisinin sırasına havale ederdi
 * ve o sıra editörde sürükleyince değişir. Sıranın kaybı burada teorik bir
 * incelik değil: "önce bütçeyi düşür, sonra duraklat" ile tersi farklı
 * sonuçlar veriyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 DERİNLİK TAVANI — EN KALABALIK v1 KURAL TAM OLARAK SIĞIYOR
 * ---------------------------------------------------------------------------
 * Zincirin derinliği `3 + aksiyon sayısı` (tetikleyici, kapsam, süzgeç + her
 * aksiyon bir seviye). `workflowActionsSchema` 10 aksiyona izin veriyor,
 * `MAX_ACTIONS_PER_GRAPH` de bilerek 10 → en derin zincir 13 düğüm.
 *
 * `MAX_GRAPH_DEPTH` bir süre 12'ydi ve o hâliyle 10 aksiyonlu bir kural
 * `GRAPH_TOO_DEEP` yiyordu: motor yalnız grafik yürüttüğü için o kural HİÇ
 * ÇALIŞAMAZDI ve kullanıcı çalıştığını sanırdı. Tavan 13'e çıkarıldı; sabitin
 * gerekçesi `automation-graph.ts`te ve oradaki cümle burada da geçerli: iki
 * sayı BİRLİKTE değişir. Aksiyon tavanı yükselirse derinlik tavanı da
 * yükselmek zorunda, yoksa aynı çelişki geri gelir.
 *
 * Alternatif (aksiyonları `filter`dan paralel bağlayıp derinliği 4'te tutmak)
 * reddedildi: yukarıdaki sıra garantisini feda ederdi. `graph-convert.spec.ts`
 * 10 aksiyonlu zinciri temiz geçtiği için test ediyor.
 *
 * ---------------------------------------------------------------------------
 * `label` neden boş bırakılıyor
 * ---------------------------------------------------------------------------
 * Düğüme "Kapsam" gibi bir ad yazmak metni İKİ DİLDEN BİRİNE sabitlemek
 * olurdu; grafik belgesi ise dilden bağımsız. `label` boşken arayüz cinsin
 * i18n adını gösteriyor (`automation-graph.ts` → `graphNodeBase`).
 */
export function graphFromLegacy(
  trigger: WorkflowTrigger,
  conditions: ConditionGroup,
  actions: readonly WorkflowAction[],
  scope: WorkflowScope,
): WorkflowGraph {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];

  /// Yerleşim SIRA numarasından türüyor — düğüm eklendikçe bir sağ adım.
  const position = (): { x: number; y: number } => ({
    x: nodes.length * LEGACY_LAYOUT_STEP_X,
    y: LEGACY_LAYOUT_Y,
  });

  const connect = (from: string, to: string): void => {
    // `fromPort` açıkça yazılıyor: şemadaki varsayılan yalnız `parse` yolunda
    // uygulanıyor ve bu fonksiyon `parse` çağırmıyor. Eksik bırakılsaydı
    // dönen nesne kendi tipini tutmazdı.
    edges.push({ id: legacyEdgeId(from, to), from, fromPort: 'out', to });
  };

  // ── Tetikleyici ──────────────────────────────────────────────────────────
  // `switch` yerine üç dallı bir ifade: `trigger.kind` daraltması `data`nın
  // tipini de daraltıyor ve `as` gerekmiyor. Bir gün `workflowTriggerSchema`ya
  // dördüncü bir cins eklenirse burası DERLENMEZ — sessizce yanlış bir cinse
  // düşmek yerine derleyici uyarsın.
  const triggerNode: GraphNode =
    trigger.kind === 'schedule'
      ? {
          id: LEGACY_NODE_IDS.trigger,
          position: position(),
          kind: 'trigger.schedule',
          data: trigger,
        }
      : trigger.kind === 'metric'
        ? {
            id: LEGACY_NODE_IDS.trigger,
            position: position(),
            kind: 'trigger.metric',
            data: trigger,
          }
        : {
            id: LEGACY_NODE_IDS.trigger,
            position: position(),
            kind: 'trigger.event',
            data: trigger,
          };
  nodes.push(triggerNode);

  // ── Kapsam ───────────────────────────────────────────────────────────────
  nodes.push({ id: LEGACY_NODE_IDS.scope, position: position(), kind: 'scope', data: scope });
  connect(LEGACY_NODE_IDS.trigger, LEGACY_NODE_IDS.scope);

  // ── Süzgeç ───────────────────────────────────────────────────────────────
  // `branch` DEĞİL `filter`: v1 kuralında koşulu geçmeyen hedeflere yapılacak
  // bir şey yok — ikinci bir dal yok ki. `branch` seçilseydi `false` portu
  // boş kalırdı ve `GRAPH_BRANCH_PORT_EMPTY` verirdi.
  nodes.push({
    id: LEGACY_NODE_IDS.filter,
    position: position(),
    kind: 'filter',
    data: conditions,
  });
  connect(LEGACY_NODE_IDS.scope, LEGACY_NODE_IDS.filter);

  // ── Aksiyon zinciri ──────────────────────────────────────────────────────
  let previous: string = LEGACY_NODE_IDS.filter;
  for (const [index, action] of actions.entries()) {
    const id = legacyActionNodeId(index + 1);
    // `kind` ile `data.type` UYUŞMAK ZORUNDA (`GRAPH_ACTION_KIND_MISMATCH`).
    // Tek kaynaktan türetiliyor: elle yazılan bir eşleme tablosu, yeni bir
    // aksiyon tipi eklendiğinde burada eksik kalırdı.
    const kind: GraphActionKind = `action.${action.type}`;
    nodes.push({ id, position: position(), kind, data: action });
    connect(previous, id);
    previous = id;
  }

  return { version: GRAPH_SCHEMA_VERSION, nodes, edges };
}

// ═══════════════════════════════════════════════════════════════════════════
// v2 → v1 (izdüşüm)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Grafiğin v1 kolonlarındaki karşılığı.
 *
 * `trigger` NULL OLABİLİR ve bu bilinçli: `trigger.manual` ile
 * `trigger.webhook` `workflowTriggerSchema`da YOK (gerekçesi
 * `automation-graph.ts` → `GRAPH_TRIGGER_KINDS`). Böyle bir grafik için uydurma
 * bir `schedule` döndürmek, kural kartında "her gün 09:00'da çalışır" yazan
 * ama aslında yalnız düğmeye basınca çalışan bir kural üretirdi. `null`
 * dönmek, çağıranı kararı vermeye zorluyor.
 */
export interface LegacyProjection {
  trigger: WorkflowTrigger | null;
  conditions: ConditionGroup;
  actions: WorkflowAction[];
  scope: WorkflowScope;
}

/**
 * 🔴 İZDÜŞÜMDE KAYBOLAN ŞEYLERİN VOKABÜLERİ.
 *
 * Dallanmalı bir grafiğin tek bir DOĞRUSAL izdüşümü YOKTUR. Kayıp kaçınılmaz;
 * kaçınılmaz olmayan şey kaybın SESSİZ olması. Kullanıcı kural listesinde
 * kuralının yarısını görür ve neden eksik olduğunu çıkaramaz — bu deponun en
 * pahalı hata sınıfı.
 *
 * Her kod bir düğüme işaret ediyor (`nodeId`) ki arayüz "şu kutu listede
 * görünmüyor" diyebilsin. `GraphShapeError` ile aynı biçim: kod + parametre,
 * düz metin değil; çeviri arayüzün işi.
 */
export const GraphProjectionLossCode = {
  /// Dallanma var; yalnız `true` dalı yansıtıldı. `false` dalının ALTI düşüyor.
  BRANCH_NOT_PROJECTED: 'BRANCH_NOT_PROJECTED',
  /// Çoklu tetikleyici — v1'de tek `trigger` kolonu var, birincisi yansıtıldı.
  TRIGGER_NOT_PROJECTED: 'TRIGGER_NOT_PROJECTED',
  /// `trigger.manual` / `trigger.webhook` — v1 kolonunda karşılığı yok.
  TRIGGER_HAS_NO_LEGACY: 'TRIGGER_HAS_NO_LEGACY',
  /// Grafikte hiç tetikleyici yok (yarım taslak).
  TRIGGER_MISSING: 'TRIGGER_MISSING',
  /// `wait` / `action.http` — v1 kuralı bunları ifade edemiyor.
  KIND_HAS_NO_LEGACY: 'KIND_HAS_NO_LEGACY',
  /// Yolda ikinci (ve sonraki) koşul düğümü. v1'de tek koşul ağacı var.
  FILTER_NOT_PROJECTED: 'FILTER_NOT_PROJECTED',
  /// Yolda hiç koşul düğümü yok → izdüşümdeki koşul ağacı BOŞ. v1 kaydetme
  /// kapısı boş koşulu reddediyor; çağıran bunu bilerek yazmalı.
  CONDITIONS_MISSING: 'CONDITIONS_MISSING',
  /// İkinci kapsam düğümü (grafik zaten `GRAPH_SCOPE_NOT_UNIQUE`).
  SCOPE_NOT_PROJECTED: 'SCOPE_NOT_PROJECTED',
  /// Hiç kapsam düğümü yok → izdüşüm hiçbir şeyi hedeflemeyen bir kapsam taşıyor.
  SCOPE_MISSING: 'SCOPE_MISSING',
  /// Birincil yolun dışında kalan her düğüm: fan-out'un ikinci kolu, dalın
  /// altı, kopuk kutu, döngüde kalan kutu.
  NODE_NOT_PROJECTED: 'NODE_NOT_PROJECTED',
} as const;
export type GraphProjectionLossCode =
  (typeof GraphProjectionLossCode)[keyof typeof GraphProjectionLossCode];

export interface GraphProjectionLoss {
  code: GraphProjectionLossCode;
  /// Hangi düğüm. "Olmayan"ı anlatan kodlarda (`SCOPE_MISSING`,
  /// `TRIGGER_MISSING`, `CONDITIONS_MISSING`) gösterilecek bir düğüm yok.
  nodeId?: string;
}

export interface GraphProjectionResult {
  projection: LegacyProjection;
  /**
   * Grafik DALLANIYOR — tek bir doğrusal izdüşüm onu tam anlatamaz.
   *
   * `losses` zaten aynı bilgiyi ayrıntısıyla taşıyor; bu bayrak tek soruluk
   * kontroller için ("kural kartına 'bu kuralın tamamı burada değil' rozeti
   * basılsın mı"). Grafikte `branch` cinsinde EN AZ BİR düğüm varsa `true` —
   * dal birincil yolda olmasa bile, çünkü o dal da bu kuralın parçası.
   */
  branched: boolean;
  /// Boş liste = izdüşüm grafiğin TAMAMINI anlatıyor.
  losses: GraphProjectionLoss[];
}

/**
 * 🔴 v1 `trigger` KOLONUNA ÇEVRİLEBİLEN TETİKLEYİCİ CİNSLERİ.
 *
 * Ayrı bir yüklem olarak dışa açık çünkü çağıranı yalnız izdüşüm değil: düğüm
 * paleti de bunu soruyor. `graphNodeKindHasEngine` ile KARIŞTIRILMAMALI —
 * ikisi ayrı sorular ve `trigger.manual` tam olarak ikisinin arasında duruyor:
 * motor onu çalıştırabiliyor (elle çalıştırma ucu var) ama v1 kolonunda
 * karşılığı YOK, yani `TRIGGER_HAS_NO_LEGACY` üretiyor ve o kayıp editörde
 * kaydetmeyi engelliyor. Palet "motoru var" diye açık bıraktığında kullanıcı
 * asla kaydedemeyeceği bir kutu ekliyordu (ölçüldü).
 *
 * Liste ELLE KOPYALANMIYOR: `legacyTriggerOf` bu yüklemin üstünde duruyor,
 * yani cins bir gün v1'e çevrilebilir olduğunda iki taraf birlikte değişiyor.
 */
export function graphTriggerKindHasLegacy(kind: string): boolean {
  return kind === 'trigger.schedule' || kind === 'trigger.metric' || kind === 'trigger.event';
}

/// v1'de karşılığı olan tetikleyici cinsleri. `data` tipi de burada daralıyor.
function legacyTriggerOf(node: GraphNode): WorkflowTrigger | null {
  if (!graphTriggerKindHasLegacy(node.kind)) return null;
  switch (node.kind) {
    case 'trigger.schedule':
    case 'trigger.metric':
    case 'trigger.event':
      return node.data;
    default:
      return null;
  }
}

type GraphActionNode = Extract<GraphNode, { kind: GraphActionKind }>;

/// `action.http` HARİÇ — onun `data`sı `HttpActionData`, `WorkflowAction`
/// değil. Ayrım `graphActionType`ten okunuyor; ikinci bir liste yazmak, yeni
/// bir aksiyon tipi eklendiğinde burayı eksik bırakırdı.
function isLegacyActionNode(node: GraphNode): node is GraphActionNode {
  return graphActionType(node.kind) !== null;
}

/**
 * 🔴 TERS YÖN — grafikten v1 kolonlarına.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden hâlâ gerekiyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Grafik kaydedilirken v1 kolonları da YAZILIYOR, çünkü onları okuyan yollar
 * duruyor ve hiçbiri grafiği anlamıyor: kural kartındaki `triggerSummary` /
 * `conditionSummary` / `actionTypes` (`automation.mappers.ts`), şablon
 * kurulumu, admin ekranları, `validateWorkflow`un guardrail hesabı. Bu yolları
 * grafik okuyacak şekilde yeniden yazmak, izdüşümü yazmaktan büyük bir iş ve
 * hiçbir yeni davranış satın almıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * BİRİNCİL YOL — tanımı ve neden bu tanım
 * ═══════════════════════════════════════════════════════════════════════════
 * Kapsam düğümünden başlanır ve tek bir yol yürünür:
 *
 *   • `branch` düğümünde `true` portu izlenir. Seçim keyfî DEĞİL: editörde
 *     "koşul tutarsa" dalı `true`; kullanıcının kuralı anlattığı cümle
 *     ("ROAS iyiyse bütçeyi artır") o daldan geçiyor. `false` dalı istisna.
 *   • `branch` dışında bir düğümden birden fazla kenar çıkıyorsa (fan-out)
 *     dizideki ilki izlenir ve kalanlar KAYIP olarak bildirilir.
 *   • Yol üzerindeki `wait` ve `action.http` düğümleri v1'e çevrilemez ama YOLU
 *     KESMEZ: altlarındaki aksiyonlar hâlâ birincil yolda.
 *
 * ⚠️ `branch`in koşulu, `true` dalı izlendiğinde `filter`la AYNI anlama
 * geliyor (geçen hedefler devam ediyor) — o yüzden yoldaki ilk koşul düğümü
 * `branch` ise koşul ağacı ONDAN alınıyor. Almasaydık kart "0 koşul" derdi ve
 * bu, koşulsuz çalıştığı sanılan bir kural demekti; hem yanlış hem tehlikeli.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * KAYBIN MUHASEBESİ — her düğümün hesabı veriliyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Fonksiyonun garantisi şu: grafikteki HER düğüm ya izdüşüme girer ya da
 * `losses` içinde kendi kimliğiyle görünür. Sessizce düşen düğüm yok. Test bu
 * değişmezi doğruluyor.
 *
 * ---------------------------------------------------------------------------
 * Ardışık `filter`lar neden BİRLEŞTİRİLMİYOR
 * ---------------------------------------------------------------------------
 * İki ardışık süzgeç mantıken bir AND: ikisinden de geçen hedefler devam
 * ediyor. Yani `{op:'AND', rules:[A, B]}` üretmek ANLAM OLARAK doğru olurdu.
 * Reddedildi çünkü birleşmiş ağaç `MAX_CONDITION_DEPTH` (3) ve
 * `MAX_CONDITION_RULES` (20) tavanlarını aşabilir; aşan bir ağaç v1 editöründe
 * BİR DAHA AÇILAMAZ.
 *
 * ⚠️ Sayaçlar (`conditionDepth`/`countRules`) artık `automation.ts`te, yani
 * burada ÇAĞRILABİLİRLER — birleştirmeyi "ölçüp sığarsa yap" hâline getirmek
 * teknik olarak mümkün. Yine de yapılmıyor: sonucu GİRDİYE BAĞLI bir dönüşüm,
 * kullanıcının aynı iki süzgeci bir kez birleşmiş bir kez ayrı görmesi demek
 * ve "kayıtlı hâli ile hesaplanan hâli" arasındaki farkı tahmin edilemez
 * kılardı. Determinizm burada şekil sabitliği anlamına da geliyor.
 *
 * Sonuç: ilk koşul düğümü yansıtılıyor, sonrakiler `FILTER_NOT_PROJECTED` ile
 * BİLDİRİLİYOR. Eksik ama sessiz değil.
 *
 * ---------------------------------------------------------------------------
 * Girdi PARSE EDİLMİŞ olmalı
 * ---------------------------------------------------------------------------
 * `edge.fromPort` şemada varsayılanlı; varsayılan yalnız `parse` yolunda
 * uygulanıyor. Veritabanındaki Json kolonundan gelen ham bir nesne
 * `graphDocumentSchema` ile ayrıştırılmadan buraya verilirse `branch`in `true`
 * portu bulunamaz ve dal KAYIP olarak bildirilir — yanlış cevap değil ama
 * eksik. `automation.mappers.ts`teki kural aynen geçerli: Json okunurken
 * ayrıştırılır.
 */
export function projectionFromGraph(graph: WorkflowGraph): GraphProjectionResult {
  const losses: GraphProjectionLoss[] = [];
  /// İzdüşüme GİREN düğümler — KİMLİKLE değil NESNEYLE tutuluyor. Aynı kimlikli
  /// ikinci bir düğüm varsa kimlik tabanlı küme onu da "izdüşüme girmiş"
  /// sayardı ve kapanış taraması onu sessizce atlardı.
  const projected = new Set<GraphNode>();
  /// Aynı (kod, düğüm) çiftini iki kez bildirmemek için.
  const reported = new Set<string>();
  /// Hakkında EN AZ BİR şey söylenmiş düğümler — kapanış taramasının girdisi.
  const mentioned = new Set<string>();

  const addLoss = (code: GraphProjectionLossCode, nodeId?: string): void => {
    if (nodeId !== undefined) {
      const key = `${code}::${nodeId}`;
      if (reported.has(key)) return;
      reported.add(key);
      mentioned.add(nodeId);
    }
    losses.push(nodeId === undefined ? { code } : { code, nodeId });
  };

  // Aynı kimlikli ikinci düğüm haritaya girmiyor (grafik zaten
  // `GRAPH_DUPLICATE_NODE_ID` ile geçersiz); ilk gören kazanıyor ki yürüyüş
  // hangi kutuyu izlediğini bilsin.
  const byId = new Map<string, GraphNode>();
  for (const node of graph.nodes) if (!byId.has(node.id)) byId.set(node.id, node);

  const outgoing = new Map<string, GraphEdge[]>();
  for (const edge of graph.edges) {
    // Ucu bilinmeyen ya da kendine dönen kenar takip edilmiyor —
    // `graphShapeErrors` bunları zaten hata sayıyor.
    if (!byId.has(edge.from) || !byId.has(edge.to) || edge.from === edge.to) continue;
    const list = outgoing.get(edge.from);
    if (list) list.push(edge);
    else outgoing.set(edge.from, [edge]);
  }

  // ── Tetikleyici ──────────────────────────────────────────────────────────
  // Birincil tetikleyici: v1'e çevrilebilen İLK tetikleyici. "İlk düğüm"
  // demek yerine "çevrilebilen ilk" demenin sebebi çoklu tetikleyici: elle
  // çalıştırma düğmesi (`trigger.manual`) yanına eklenmiş bir zamanlama varsa,
  // kartta zamanlamayı göstermek elle çalıştırmayı göstermekten bilgilendirici.
  const triggerNodes = graph.nodes.filter((node) => isGraphTriggerKind(node.kind));
  const primaryTrigger =
    triggerNodes.find((node) => legacyTriggerOf(node) !== null) ?? triggerNodes[0] ?? null;

  let trigger: WorkflowTrigger | null = null;
  if (primaryTrigger === null) {
    addLoss(GraphProjectionLossCode.TRIGGER_MISSING);
  } else {
    trigger = legacyTriggerOf(primaryTrigger);
    if (trigger === null) addLoss(GraphProjectionLossCode.TRIGGER_HAS_NO_LEGACY, primaryTrigger.id);
    else projected.add(primaryTrigger);
  }
  for (const node of triggerNodes) {
    if (node.id === primaryTrigger?.id) continue;
    addLoss(GraphProjectionLossCode.TRIGGER_NOT_PROJECTED, node.id);
  }

  // ── Kapsam ───────────────────────────────────────────────────────────────
  // Tip süzgeci (`node is …`) şart: düz bir `filter` `GraphNode[]` döndürür ve
  // `data` birleşim tipinde kalır — kapsam düğümünün `data`sını okumak için
  // daraltma gerekiyor.
  const scopeNodes = graph.nodes.filter(
    (node): node is Extract<GraphNode, { kind: 'scope' }> => node.kind === 'scope',
  );
  const primaryScope = scopeNodes[0] ?? null;
  // Kapsamsız izdüşümün varsayılanı `selected` + BOŞ liste, yani HİÇBİR
  // hedef. `all_active` seçilseydi yarım bir taslağın izdüşümü "workspace'teki
  // tüm aktif kampanyalar" derdi ve güvenli yön açıkça diğeri.
  // (`automation.mappers.ts` → `FALLBACK_SCOPE` aynı şekli aynı gerekçeyle
  // kullanıyor; oradan import edilemiyor, bağımlılık yönü tek taraflı.)
  let scope: WorkflowScope = {
    mode: 'selected',
    adAccountIds: [],
    campaignIds: [],
    adSetIds: [],
    level: 'CAMPAIGN',
    minSpend: 0,
  };
  if (primaryScope === null) {
    addLoss(GraphProjectionLossCode.SCOPE_MISSING);
  } else {
    scope = primaryScope.data;
    projected.add(primaryScope);
  }
  for (const node of scopeNodes) {
    if (node.id === primaryScope?.id) continue;
    addLoss(GraphProjectionLossCode.SCOPE_NOT_PROJECTED, node.id);
  }

  // ── Birincil yol ─────────────────────────────────────────────────────────
  let conditions: ConditionGroup | null = null;
  const actions: WorkflowAction[] = [];

  // Kapsam yoksa tetikleyiciden başlanıyor: yarım bir taslakta bile aşağıdaki
  // aksiyonlar kartta görünsün. `null` başlangıç yalnız hiç düğüm yoksa olur.
  let current: GraphNode | null = primaryScope ?? primaryTrigger;
  const visited = new Set<string>();

  while (current !== null) {
    // Döngü koruması. Grafik DAG olmak zorunda (`GRAPH_CYCLE`) ama bu fonksiyon
    // doğrulanmamış bir taslakla da çağrılabiliyor ve sonsuz döngüye girmesi
    // bir uç isteğini asardı.
    if (visited.has(current.id)) break;
    visited.add(current.id);

    const node: GraphNode = current;

    if (node.kind === 'filter' || node.kind === 'branch') {
      if (node.kind === 'branch') addLoss(GraphProjectionLossCode.BRANCH_NOT_PROJECTED, node.id);
      if (conditions === null) {
        conditions = node.data;
        projected.add(node);
      } else {
        addLoss(GraphProjectionLossCode.FILTER_NOT_PROJECTED, node.id);
      }
    } else if (isLegacyActionNode(node)) {
      actions.push(node.data);
      projected.add(node);
    } else if (node.kind !== 'scope' && !isGraphTriggerKind(node.kind)) {
      // Geriye `wait` ve `action.http` kalıyor. Kapsam ve tetikleyici
      // yukarıda hesaplandı; burada ikinci kez bildirilmiyorlar.
      addLoss(GraphProjectionLossCode.KIND_HAS_NO_LEGACY, node.id);
    }

    const out = outgoing.get(node.id) ?? [];
    const next = node.kind === 'branch' ? out.find((edge) => edge.fromPort === 'true') : out[0];
    current = next === undefined ? null : (byId.get(next.to) ?? null);
  }

  if (conditions === null) {
    addLoss(GraphProjectionLossCode.CONDITIONS_MISSING);
    conditions = { kind: 'group', op: 'AND', rules: [] };
  }

  // ── Yolun dışında kalan her şey ──────────────────────────────────────────
  // Muhasebenin kapanışı: izdüşüme girmemiş ve hakkında hiçbir şey
  // söylenmemiş düğüm kalmasın.
  //
  // 🔴 `byId` üzerinde DEĞİL `graph.nodes` üzerinde dönülüyor. `byId` aynı
  // kimlikli ikinci düğümü zaten atmış olduğu için tarama onu hiç görmüyordu:
  // `[{id:'x',action.pause}, {id:'x',action.resume}]` grafiğinde `resume`
  // ne izdüşüme giriyor ne `losses`ta görünüyordu — fonksiyonun ilan ettiği
  // "sessizce düşen düğüm yok" garantisi tam da o durumda tutmuyordu. Grafik
  // `GRAPH_DUPLICATE_NODE_ID` ile zaten geçersiz ama bu fonksiyon
  // DOĞRULANMAMIŞ taslakla da çağrılabiliyor; garanti orada da geçerli olmalı.
  // (`addLoss` aynı (kod, kimlik) çiftini iki kez basmıyor, yani tekrar eden
  // kimlik tek satır üretiyor.)
  for (const node of graph.nodes) {
    if (projected.has(node) || mentioned.has(node.id)) continue;
    addLoss(GraphProjectionLossCode.NODE_NOT_PROJECTED, node.id);
  }

  return {
    projection: { trigger, conditions, actions, scope },
    branched: graph.nodes.some((node) => node.kind === 'branch'),
    losses,
  };
}
